<template>
  <div class="relative min-h-screen pb-24 bg-[#fafbfc] overflow-x-hidden">
    <!-- Loader animasi di tengah layar -->
    <div
      v-if="isLoading"
      class="fixed inset-0 flex items-center justify-center bg-[#fafbfc] z-[9999]"
    >
      <LottieLoader :src="lottieJson" />
    </div>

    <!-- Error Message -->
    <div
      v-if="errorMessage"
      class="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] bg-red-500 text-white px-6 py-3 rounded-xl shadow-lg"
    >
      {{ errorMessage }}
    </div>

    <!-- Header with Banner - Modern Minimal Design -->
    <div
      class="relative bg-gradient-to-br from-[#5A6670] via-[#3A5A7A] to-[#2C3E50] overflow-hidden"
    >
      <!-- Animated Decorative Elements -->
      <div class="absolute inset-0 opacity-30">
        <div
          class="absolute -top-24 -right-24 w-64 h-64 bg-[#7FA3D1] rounded-full blur-3xl opacity-20 animate-pulse"
        />
        <div
          class="absolute -bottom-24 -left-24 w-72 h-72 bg-[#5A9FBF] rounded-full blur-3xl opacity-20 animate-pulse"
          style="animation-delay: 2s"
        />
      </div>

      <!-- Header Content - Mobile Optimized -->
      <div
        class="relative z-10 px-4 py-8 flex flex-col items-center justify-center text-center"
      >
        <div
          class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur-md mb-3 border border-white/30"
        >
          <svg
            class="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
            />
          </svg>
        </div>
        <h1
          class="text-2xl sm:text-3xl font-bold text-white mb-1 drop-shadow-lg"
        >
          Menu Pemesanan
        </h1>
        <p class="text-xs sm:text-sm text-white/80 font-medium tracking-wide">
          Pilih menu yang ingin Anda pesan
        </p>
      </div>
    </div>

    <!-- Search & Filter Bar - Modern Floating Card Mobile Optimized -->
    <div class="relative z-40 px-3 sm:px-4 -mt-6 mb-6 sm:mb-8">
      <div
        class="px-4 py-4 sm:py-5 max-w-full bg-white/98 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20"
      >
        <!-- Search Bar -->
        <div class="flex items-center gap-2 mb-3">
          <div class="flex-1 relative group">
            <svg
              class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-[#7f8c8d] group-focus-within:text-[#5A6670] transition-colors"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Cari..."
              class="w-full pl-10 pr-3 py-2.5 sm:py-3 rounded-xl border border-[#e8ecf1] bg-[#fafbfc] text-[#2c3e50] placeholder:text-[#7f8c8d] focus:outline-none focus:border-[#5A6670] focus:bg-white focus:ring-2 focus:ring-[#5A6670]/20 transition-all text-sm font-medium"
              @input="onSearchInput"
            >
          </div>

          <!-- Refresh Button -->
          <button
            :disabled="isRefreshing"
            class="flex-shrink-0 p-2.5 sm:p-3 rounded-xl bg-white border-2 border-[#e8ecf1] hover:border-[#5A6670] text-[#5A6670] hover:bg-[#fafbfc] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
            @click="refreshMenu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4 sm:w-5 sm:h-5"
              :class="{ 'animate-spin': isRefreshing }"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>

        <!-- Category Filter - Horizontal Scroll -->
        <div class="overflow-x-auto scrollbar-hide">
          <div class="flex gap-2 min-w-max p-2">
            <button
              :class="[
                'px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg whitespace-nowrap text-xs sm:text-sm font-bold transition-all flex-shrink-0 shadow-sm',
                selectedCategory === 0
                  ? 'bg-[#3A5A7A] text-white shadow-md ring-2 ring-[#3A5A7A]/30'
                  : 'bg-[#f0f4f8] text-[#2c3e50] hover:bg-[#e8ecf1] border border-[#e8ecf1]',
              ]"
              @click="selectCategory(0)"
            >
              📋 Semua
            </button>
            <button
              :class="[
                'px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg whitespace-nowrap text-xs sm:text-sm font-bold transition-all flex-shrink-0 shadow-sm',
                selectedCategory === 'best'
                  ? 'bg-[#3A5A7A] text-white shadow-md ring-2 ring-[#3A5A7A]/30'
                  : 'bg-[#f0f4f8] text-[#2c3e50] hover:bg-[#e8ecf1] border border-[#e8ecf1]',
              ]"
              @click="selectCategory('best')"
            >
              Best Seller
            </button>
            <button
              v-if="locationArea === 'Billiard'"
              :class="[
                'px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg whitespace-nowrap text-xs sm:text-sm font-bold transition-all flex-shrink-0 shadow-sm',
                selectedCategory === 'billiard'
                  ? 'bg-[#3A5A7A] text-white shadow-md ring-2 ring-[#3A5A7A]/30'
                  : 'bg-[#f0f4f8] text-[#2c3e50] hover:bg-[#e8ecf1] border border-[#e8ecf1]',
              ]"
              @click="selectCategory('billiard')"
            >
              Bir
            </button>
            <button
              v-for="category in categories"
              :key="category.id"
              :class="[
                'px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg whitespace-nowrap text-xs sm:text-sm font-bold transition-all flex-shrink-0 shadow-sm',
                selectedCategory === category.id
                  ? 'bg-[#3A5A7A] text-white shadow-md ring-2 ring-[#3A5A7A]/30'
                  : 'bg-[#f0f4f8] text-[#2c3e50] hover:bg-[#e8ecf1] border border-[#e8ecf1]',
              ]"
              @click="selectCategory(category.id)"
            >
              {{ category.name }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Menu Content - Mobile Optimized -->
    <div
      class="px-3 sm:px-4 py-6 sm:py-12 max-w-full"
      :class="{ 'pb-32': cartCount > 0 }"
    >
      <!-- Skeleton Loading saat refresh atau transisi kategori -->
      <div
        v-if="isRefreshing || isTransitioning"
        class="relative"
      >
        <div v-if="tempSelectedCategory === 0 || selectedCategory === 0">
          <div
            v-for="category in categories.slice(0, 2)"
            :key="'skeleton-' + category.id"
            class="mb-8 sm:mb-12"
          >
            <div
              class="h-6 sm:h-8 w-40 bg-[#e8ecf1] rounded-lg mb-4 sm:mb-6 skeleton-shimmer"
            />
            <div
              class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5"
            >
              <div
                v-for="i in 8"
                :key="'skeleton-item-' + i"
                class="skeleton-card"
              >
                <div class="skeleton-image" />
                <div class="skeleton-content">
                  <div class="skeleton-title" />
                  <div class="skeleton-price" />
                  <div class="skeleton-button" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <div
            class="h-6 sm:h-8 w-40 bg-[#e8ecf1] rounded-lg mb-4 sm:mb-6 skeleton-shimmer"
          />
          <div
            class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5"
          >
            <div
              v-for="i in 10"
              :key="'skeleton-item-' + i"
              class="skeleton-card"
            >
              <div class="skeleton-image" />
              <div class="skeleton-content">
                <div class="skeleton-title" />
                <div class="skeleton-price" />
                <div class="skeleton-button" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Menu Items -->
      <div
        v-show="!isRefreshing && !isTransitioning"
        class="relative"
      >
        <transition
          :name="transitionName"
          mode="out-in"
        >
          <div
            :key="selectedCategory"
            class="relative"
          >
            <!-- All Categories View -->
            <div v-if="selectedCategory === 0">
              <div
                v-if="!anyResults"
                class="flex flex-col items-center justify-center text-center py-16 sm:py-24"
              >
                <div class="mb-4 sm:mb-6">
                  <svg
                    class="w-16 h-16 sm:w-24 sm:h-24 text-[#d8dfe7] mx-auto"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1"
                      d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p
                  class="text-lg sm:text-2xl font-bold text-[#3A444E] mb-1 sm:mb-2"
                >
                  Menu Tidak Tersedia
                </p>
                <p class="text-sm sm:text-base text-[#7f8c8d]">
                  Maaf, saat ini belum ada menu yang sesuai dengan pencarian
                  Anda
                </p>
              </div>
              <div v-else>
                <div
                  v-for="category in categoriesToShow"
                  :key="category.id"
                  class="mb-8 sm:mb-14"
                >
                  <div
                    class="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 pb-2 sm:pb-3 border-b-2 border-[#e8ecf1]"
                  >
                    <h2 class="text-lg sm:text-2xl font-bold text-[#2c3e50]">
                      {{ category.name }}
                    </h2>
                    <span
                      class="inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#3A5A7A] text-white text-xs sm:text-sm font-bold"
                    >
                      {{ getCachedFilteredMenus(category.id).length }}
                    </span>
                  </div>
                  <div
                    class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5"
                  >
                    <MenuItem
                      v-for="item in getCachedFilteredMenus(category.id)"
                      :key="`${item.id}-${item.categoryId}`"
                      :item="item"
                      :is-out-of-stock="item.isOutOfStock"
                      :is-best-seller="item.isBestSeller"
                      :active-variant-id="activeVariantId"
                      :set-active-variant="setActiveVariant"
                      :get-menu-variants="getMenuVariants"
                      @add-to-cart="handleAddToCart"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Best Seller or Specific Category View -->
            <div v-else>
              <div
                class="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8 pb-3 sm:pb-4 border-b-2 border-[#e8ecf1]"
              >
                <span
                  v-if="selectedCategory === 'best'"
                  class="text-2xl sm:text-3xl"
                >⭐</span>
                <h2 class="text-xl sm:text-3xl font-bold text-[#2c3e50]">
                  {{ currentCategoryName }}
                </h2>
                <span
                  class="inline-flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-[#3A5A7A] text-white text-xs sm:text-sm font-bold ml-auto"
                >
                  {{ filteredMenus.length }}
                </span>
              </div>

              <!-- Empty State -->
              <div
                v-if="filteredMenus.length === 0"
                class="flex flex-col items-center justify-center text-center py-16 sm:py-24"
              >
                <div class="mb-4 sm:mb-6">
                  <svg
                    class="w-16 h-16 sm:w-24 sm:h-24 text-[#d8dfe7] mx-auto"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1"
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                </div>
                <p
                  class="text-lg sm:text-2xl font-bold text-[#3A444E] mb-1 sm:mb-2"
                >
                  {{
                    selectedCategory === "best"
                      ? "Belum Ada Favorit"
                      : "Menu Kosong"
                  }}
                </p>
                <p class="text-sm sm:text-base text-[#7f8c8d]">
                  {{
                    selectedCategory === "best"
                      ? "Item favorit Anda akan muncul di sini"
                      : "Tidak ada menu tersedia saat ini"
                  }}
                </p>
              </div>

              <!-- Menu Grid -->
              <div
                v-else
                class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5"
              >
                <MenuItem
                  v-for="item in filteredMenus"
                  :key="`${item.id}-${item.categoryId}`"
                  :item="item"
                  :is-out-of-stock="item.isOutOfStock"
                  :is-best-seller="item.isBestSeller"
                  :active-variant-id="activeVariantId"
                  :set-active-variant="setActiveVariant"
                  :get-menu-variants="getMenuVariants"
                  @add-to-cart="handleAddToCart"
                />
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Sticky Cart Bar - Premium Design Mobile Optimized -->
    <transition name="slide-up">
      <div
        v-if="cartCount > 0"
        class="fixed bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4 z-40"
      >
        <div
          class="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-[#5A6670] to-[#3A5A7A] shadow-2xl rounded-xl sm:rounded-2xl backdrop-blur-md border border-white/10"
        >
          <div class="flex items-center justify-between max-w-full gap-3">
            <!-- Cart Info -->
            <div class="flex flex-col gap-0.5 sm:gap-1 min-w-0">
              <p
                class="text-xs font-semibold text-white/70 uppercase tracking-wide"
              >
                Keranjang
              </p>
              <div class="flex items-baseline gap-2">
                <span class="text-sm sm:text-lg font-bold text-white">{{ cartCount }} item</span>
                <span class="text-base sm:text-xl font-bold text-white truncate">Rp {{ formatPrice(getTotalPrice()) }}</span>
              </div>
            </div>

            <!-- Next Button -->
            <button
              class="flex-shrink-0 flex items-center justify-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white text-[#5A6670] rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95 whitespace-nowrap"
              @click="goToCart"
            >
              <span>Lanjut</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ✅ TAMBAHAN: Variant Modal -->
    <VariantModal
      :is-open="isModalOpen"
      :product="selectedProduct"
      :variants="selectedProductVariants"
      :additionals="selectedProduct.additionals || []"
      @close="closeModal"
      @add-to-cart="handleAddFromModal"
    />
  </div>
</template>

<script>
import MenuItem from "@/components/MenuItem.vue";
import LottieLoader from "@/components/LottieLoader.vue";
import VariantModal from "@/components/VariantModal.vue";
import api from "@/api";
import lottieJson from "../assets/loader.json";

const CONFIG = {
  BEST_SELLER_LIMIT: 10,
  BEST_SELLER_DAYS: 90,
  ANIMATION: {
    SEARCH_DEBOUNCE: 300,
    CART_ANIMATION: 600,
    REFRESH_DELAY: 500,
    CATEGORY_TRANSITION_START: 150,
    CATEGORY_TRANSITION_END: 500,
  },
  ASSETS_BASE_URL: "https://vamos-api-v2.sejadikopi.com/storage",
  SPECIAL_CATEGORIES: {
    DIMSUM: ["dimsum", "dim sum"],
  },
};

CONFIG.LOGO_URL = `${CONFIG.ASSETS_BASE_URL}/Logo/vamos_circle.png`;
CONFIG.BANNER_URL = `${CONFIG.ASSETS_BASE_URL}/images/banner.jpg`;

export default {
  name: "MenuPage",
  components: {
    MenuItem,
    LottieLoader,
    VariantModal,
  },
  emits: ["add-to-cart"],

  data() {
    return {
      config: CONFIG,
      searchQuery: "",
      debouncedSearchQuery: "",
      searchDebounceTimer: null,
      categories: [],
      selectedCategory: "best",
      previousCategory: 0,
      slideDirection: "left",
      menus: [],
      bestSellerIds: [],
      billiardMenus: [],
      locationArea: null,
      specialCategories: {
        dimsum: [],
      },
      cart: [],
      cartCount: 0,
      activeVariantId: null,
      isCartAnimating: false,
      isLoading: true,
      isRefreshing: false,
      isTransitioning: false,
      tempSelectedCategory: null,
      errorMessage: null,
      menuCache: new Map(),
      lottieJson,
      // ✅ TAMBAHAN: Modal State
      isModalOpen: false,
      selectedProduct: {},
      selectedProductVariants: [],
    };
  },

  computed: {
    queryString() {
      return (this.debouncedSearchQuery || "").trim().toLowerCase();
    },

    allCategories() {
      return [{ id: 0, name: "Semua" }, ...this.categories];
    },

    currentCategoryName() {
      if (this.selectedCategory === "best") return "Best Seller";
      if (this.selectedCategory === "billiard") return "Billiard only";
      const category = this.categories.find(
        (c) => c.id === this.selectedCategory
      );
      return category ? category.name : "";
    },

    categoryOrder() {
      const order = ["best"];
      if (this.locationArea === "Billiard") {
        order.push("billiard");
      }
      order.push(0, ...this.categories.map((c) => c.id));
      return order;
    },

    transitionName() {
      return this.slideDirection === "left" ? "slide-left" : "slide-right";
    },

    filteredMenus() {
      const q = this.queryString;
      if (this.selectedCategory === "best") {
        return this.menus.filter(
          (menu) =>
            this.bestSellerIds.includes(menu.id) &&
            (menu.name || "").toLowerCase().includes(q)
        );
      }
      if (this.selectedCategory === "billiard") {
        return this.billiardMenus.filter(
          (menu) => (menu.name || "").toLowerCase().includes(q)
        );
      }
      return this.menus.filter(
        (menu) =>
          menu.categoryId === this.selectedCategory &&
          (menu.name || "").toLowerCase().includes(q) &&
          (this.locationArea === "Billiard" || !menu.only_in_billiard)
      );
    },

    anyResults() {
      const q = this.queryString;
      if (!q) return true;

      if (this.selectedCategory === 0) {
        return this.menus.some((menu) =>
          (menu.name || "").toLowerCase().includes(q)
        );
      } else {
        return this.menus.some(
          (menu) =>
            menu.categoryId === this.selectedCategory &&
            (menu.name || "").toLowerCase().includes(q)
        );
      }
    },

    categoriesToShow() {
      const q = this.queryString;
      if (!q) return this.categories;

      return this.categories.filter((cat) => {
        const cachedMenus = this.getCachedFilteredMenus(cat.id);
        return cachedMenus.length > 0;
      });
    },

    headerBackgroundStyle() {
      return {
        backgroundImage: `url('${CONFIG.BANNER_URL}')`,
      };
    },
  },

  watch: {
    debouncedSearchQuery() {
      this.menuCache.clear();
    },
  },

  async mounted() {
    this.locationArea = localStorage.getItem("locationArea") || null;
    await this.initializeApp();
  },

  methods: {
    async initializeApp() {
      try {
        const promises = [
          this.loadCategories(),
          this.loadMenuData(),
          this.loadBestSellers(),
        ];

        if (this.locationArea === "Billiard") {
          promises.push(this.loadBilliardMenus());
        }

        await Promise.all(promises);

        this.loadCart();

        console.log("App initialized successfully");
      } catch (error) {
        console.error("Error initializing app:", error);
        this.showError("Gagal memuat data. Silakan refresh halaman.");
      } finally {
        this.isLoading = false;
      }
    },

    async loadCategories() {
      try {
        const response = await api.get("/categories");
        const items = response.data?.data ?? response.data;

        if (!items || !Array.isArray(items))
          throw new Error("No data received");

        this.categories = items
          .map((cat) => ({
            id: cat.id,
            name: cat.name || cat.nama || "",
            urutan:
              typeof cat.urutan !== "undefined"
                ? cat.urutan
                : typeof cat.order !== "undefined"
                ? cat.order
                : 99,
          }))
          .sort((a, b) => {
            if (a.urutan !== b.urutan) {
              return a.urutan - b.urutan;
            }
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
          });
      } catch (error) {
        console.error("Error loading categories:", error);
        this.showError("Gagal memuat kategori.");
      }
    },

    async loadMenuData() {
      try {
        const menuResponse = await api.get("/menus?sort_by=created_at");

        this.menus = menuResponse.data.data.map((item) => ({
          ...item,
          image: item.image_url,
          name: item.name,
          price: item.price,
          description: item.description,
          categoryId: item.category_id,
          variants: item.variants || [],
          additionals: item.additionals || [],
          isOutOfStock: !item.is_available,
          isBestSeller: item.is_best_seller,
          only_in_billiard: item.only_in_billiard || false,
        }));

        console.log("Menu loaded:", this.menus.length);
      } catch (error) {
        console.error("Error loading menu:", error);
        throw error;
      }
    },

    async loadBestSellers() {
      try {
        const response = await api.get(
          `/menus?best_seller=1&sort_by=created_at`
        );
        this.bestSellerIds = response.data.data.map((item) => item.id);
      } catch (error) {
        console.error("Error loading best sellers:", error);
        this.showError("Gagal memuat best seller.");
      }
    },

    async loadBilliardMenus() {
      try {
        const response = await api.get("/menus?is_billiard=1");
        this.billiardMenus = response.data.data.map((item) => ({
          ...item,
          image: item.image_url,
          name: item.name,
          price: item.price,
          description: item.description,
          categoryId: item.category_id,
          variants: item.variants || [],
          additionals: item.additionals || [],
          isOutOfStock: !item.is_available,
          isBestSeller: item.is_best_seller,
          only_in_billiard: item.only_in_billiard || false,
        }));
        console.log("Billiard menus loaded:", this.billiardMenus.length);
      } catch (error) {
        console.error("Error loading billiard menus:", error);
        this.showError("Gagal memuat menu billiard.");
      }
    },

    async refreshMenu() {
      if (this.isRefreshing) return;

      this.isRefreshing = true;
      this.menuCache.clear();

      try {
        const promises = [
          this.loadCategories(),
          this.loadMenuData(),
          this.loadBestSellers(),
        ];

        if (this.locationArea === "Billiard") {
          promises.push(this.loadBilliardMenus());
        }

        await Promise.all(promises);

        await new Promise((resolve) =>
          setTimeout(resolve, CONFIG.ANIMATION.REFRESH_DELAY)
        );

        console.log("Menu refreshed");
      } catch (error) {
        console.error("Error refreshing menu:", error);
        this.showError("Gagal memperbarui menu. Silakan coba lagi.");
      } finally {
        this.isRefreshing = false;
      }
    },

    detectSpecialCategories() {
      this.specialCategories.dimsum = this.categories
        .filter((cat) => {
          const name = cat.name.toLowerCase();
          return CONFIG.SPECIAL_CATEGORIES.DIMSUM.some((keyword) =>
            name.includes(keyword)
          );
        })
        .map((cat) => cat.id);

      if (this.specialCategories.dimsum.length > 0) {
        console.log(
          "Dimsum categories detected:",
          this.specialCategories.dimsum
        );
      }
    },

    selectCategory(id) {
      if (id === this.selectedCategory) return;

      const order = this.categoryOrder;
      const prevIdx = order.indexOf(this.selectedCategory);
      const nextIdx = order.indexOf(id);

      this.slideDirection = nextIdx > prevIdx ? "left" : "right";
      this.tempSelectedCategory = id;

      setTimeout(() => {
        this.selectedCategory = id;
        this.isTransitioning = true;

        setTimeout(() => {
          this.isTransitioning = false;
          this.tempSelectedCategory = null;
        }, CONFIG.ANIMATION.CATEGORY_TRANSITION_END);
      }, CONFIG.ANIMATION.CATEGORY_TRANSITION_START);
    },

    onSearchInput() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearchQuery = this.searchQuery;
      }, CONFIG.ANIMATION.SEARCH_DEBOUNCE);
    },

    getCachedFilteredMenus(catId) {
      const cacheKey = `${catId}-${this.queryString}`;

      if (this.menuCache.has(cacheKey)) {
        return this.menuCache.get(cacheKey);
      }

      const q = this.queryString;
      const filtered = this.menus.filter(
        (menu) =>
          menu.categoryId === catId &&
          (menu.name || "").toLowerCase().includes(q) &&
          (this.locationArea === "Billiard" || !menu.only_in_billiard)
      );

      this.menuCache.set(cacheKey, filtered);
      return filtered;
    },

    loadCart() {
      this.cart = JSON.parse(localStorage.getItem("cart") || "[]");
      this.cartCount = this.cart.reduce(
        (sum, item) => sum + (item.quantity || 0),
        0
      );
    },

    addToCart(menu) {
      let cart = JSON.parse(localStorage.getItem("cart") || "[]");

      const menuVarian = menu.varian || null;
      const quantity = menu.quantity && menu.quantity > 0 ? menu.quantity : 1;

      const idx = cart.findIndex(
        (i) =>
          i.id === menu.id &&
          i.isLocked === (menu.isLocked || false) &&
          ((!i.varian && !menu.varian) ||
            (i.varian && menu.varian && i.varian.id === menu.varian.id))
      );

      if (idx !== -1 && !cart[idx].isLocked && !menu.isLocked) {
        cart[idx].quantity += quantity;
      } else {
        const newItem = {
          id: menu.id,
          name: menu.name,
          price: menu.price,
          basePrice: menu.price || menu.basePrice,
          kategori_id: menu.kategori_id,
          varian: menuVarian,
          quantity,
          note: menu.note || "",
          isLocked: menu.isLocked || false,
          selectedAdditionalId: menu.selectedAdditionalId || null,
          additionals: menu.additionals || [],
        };

        cart.push(newItem);
      }

      localStorage.setItem("cart", JSON.stringify(cart));
      this.loadCart();
      this.triggerCartAnimation();
    },

    // ✅ UPDATE: Handle Add To Cart - Buka Modal kalau ada variant
    handleAddToCart(item) {
      const variants = this.getMenuVariants(item);

      // Jika ada variants, buka modal
      if (variants && variants.length > 0) {
        this.openModal(item, variants);
        return;
      }

      // Jika tidak ada variant, langsung add to cart
      const isDimsum = this.isCategorySpecial(item.kategori_id, "dimsum");

      if (isDimsum) {
        const menuWithDefaults = {
          ...item,
          isLocked: item.isLocked || false,
          additionalPrice: item.additionalPrice || 0,
          dimsumOption: item.dimsumOption || "ori",
          basePrice: item.price,
        };
        this.addToCart(menuWithDefaults);
      } else {
        this.addToCart(item);
      }

      this.activeVariantId = null;
    },

    // ✅ TAMBAHAN: Open Modal
    openModal(product, variants) {
      this.selectedProduct = {
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        description: product.description, // ⭐ TAMBAH DESCRIPTION
        categoryId: product.categoryId,
        isOutOfStock: product.isOutOfStock,
        additionals: product.additionals || [], // ⭐ Ini udah bener, cuma pastikan product.additionals ada
      };
      this.selectedProductVariants = variants;
      this.isModalOpen = true;
    },

    // ✅ TAMBAHAN: Close Modal
    closeModal() {
      this.isModalOpen = false;
      this.selectedProduct = {};
      this.selectedProductVariants = [];
    },

    // ✅ TAMBAHAN: Handle Add from Modal
    handleAddFromModal(cartItem) {
      this.addToCart(cartItem);
      this.closeModal();
    },

    triggerCartAnimation() {
      this.isCartAnimating = true;
      setTimeout(() => {
        this.isCartAnimating = false;
      }, CONFIG.ANIMATION.CART_ANIMATION);
    },

    goToCart() {
      this.$router.push({ name: "CartPage" });
    },

    getMenuVariants(menu) {
      if (!menu) return [];
      try {
        if (menu.variants) {
          const variants = menu.variants.map((variant) => {
            if (typeof variant === "string") {
              const tempId = variant.toLowerCase().replace(/\s+/g, "-");
              return { id: tempId, name: variant };
            }
            return { id: variant.id || "", name: variant.name || "" };
          });
          return Array.isArray(variants) ? variants : [];
        }
      } catch (error) {
        console.error("Error parsing variants for menu:", menu.id, error);
      }
      return [];
    },

    setActiveVariant(id) {
      this.activeVariantId = id;
    },

    isCategorySpecial(categoryId, type) {
      return this.specialCategories[type]?.includes(categoryId) || false;
    },

    showError(message) {
      this.errorMessage = message;
      setTimeout(() => {
        this.errorMessage = null;
      }, 5000);
    },

    formatPrice(price) {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
        .format(price)
        .replace("Rp", "")
        .trim();
    },

    getTotalPrice() {
      return this.cart.reduce((total, item) => {
        const basePrice = item.basePrice || item.price || 0;
        const additionalPrice = item.additionalPrice || 0;
        const itemTotal = (basePrice + additionalPrice) * (item.quantity || 1);
        return total + itemTotal;
      }, 0);
    },
  },
};
</script>

<style scoped>
.bg-cream {
  background-color: #fafbfc;
}
.bg-latte {
  background-color: #e8ecf1;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
::-webkit-scrollbar {
  display: none !important;
}
* {
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

/* Skeleton Loading - Fresh Pulse Design */
.skeleton-card {
  background: white;
  border-radius: 1.25rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e8ecf1;
}

.skeleton-image {
  width: 100%;
  padding-top: 100%;
  background: linear-gradient(90deg, #f0f4f8 25%, #e8ecf1 50%, #f0f4f8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-content {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-title {
  height: 0.875rem;
  background: linear-gradient(90deg, #f0f4f8 25%, #e8ecf1 50%, #f0f4f8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.5rem;
  margin-bottom: 0.25rem;
}

.skeleton-price {
  height: 0.75rem;
  width: 60%;
  background: linear-gradient(90deg, #f0f4f8 25%, #e8ecf1 50%, #f0f4f8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.5rem;
}

.skeleton-button {
  height: 2.25rem;
  background: linear-gradient(90deg, #f0f4f8 25%, #e8ecf1 50%, #f0f4f8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.75rem;
  margin-top: 0.25rem;
}

.skeleton-shimmer {
  background: linear-gradient(90deg, #f0f4f8 25%, #e8ecf1 50%, #f0f4f8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 0.5rem;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Slide Transitions - Smooth & Fresh */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform, opacity;
}

.slide-left-enter-from {
  transform: translate3d(40px, 0, 0);
  opacity: 0;
}
.slide-left-leave-to {
  transform: translate3d(-40px, 0, 0);
  opacity: 0;
}

.slide-right-enter-from {
  transform: translate3d(-40px, 0, 0);
  opacity: 0;
}
.slide-right-leave-to {
  transform: translate3d(40px, 0, 0);
  opacity: 0;
}

/* Slide Up Animation - Cart Bar */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  will-change: transform, opacity;
}

.slide-up-enter-from {
  transform: translateY(140%);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(140%);
  opacity: 0;
}
</style>
